<!--
  CR V3 课后资料 Overlay（D29）：不强制跳 /end，不断开 WS
-->
<template>
  <button
    v-if="visible && !open"
    type="button"
    class="post-class-fab"
    @click="open = true"
  >
    课后资料
  </button>

  <a-drawer
    v-model:open="open"
    placement="bottom"
    :height="drawerHeight"
    :mask-closable="true"
    :keyboard="true"
    root-class-name="cr-v3-post-class-drawer-root"
    :title="drawerTitle"
  >
    <div class="post-class-body">
      <p v-if="hints.waitingEnded" class="hint hint--waiting">
        {{ hints.waitingEndedText }}
      </p>
      <p v-if="hints.pendingMinutes" class="hint hint--pending">
        {{ hints.pendingMinutesText }}
      </p>
      <p v-if="hints.partialFailure" class="hint hint--partial">
        {{ hints.partialFailureText }}
      </p>

      <!-- 课后资料 QR 下载：Web 一期暂不需要（enableQrDownload=false） -->
      <template v-if="enableQrDownload">
        <div v-if="!qrItems.length && !hints.waitingEnded && !hints.pendingMinutes" class="empty">
          暂无匹配当前语言的下载资料
        </div>

        <div v-for="item in qrItems" :key="item.id" class="qr-block">
          <div class="qr-label">{{ item.label }}</div>
          <QRCodeShare :url="item.url" :compact="true" />
        </div>
      </template>
    </div>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import QRCodeShare from '@/modules/speech-translation/components/QRCodeShare.vue'
import type { CrV3SessionSnapshot } from '../types'
import {
  buildPostClassQrItems,
  resolvePostClassHints,
  shouldShowPostClassDrawer,
} from '../utils/build-post-class-qr-items'

const props = defineProps<{
  session: CrV3SessionSnapshot
  /** 观众所选短码 / languageCode2（D23） */
  viewerLangCodes: string[]
  sourceLangCode: string
  /** 是否展示课后资料 QR（Web 观众端一期暂关） */
  enableQrDownload?: boolean
}>()

const open = ref(false)

const enableQrDownload = computed(() => props.enableQrDownload ?? false)

const visible = computed(
  () => enableQrDownload.value && shouldShowPostClassDrawer(props.session),
)

watch(
  visible,
  (v) => {
    if (v) {
      open.value = true
    } else {
      open.value = false
    }
  },
  { immediate: true },
)

const qrItems = computed(() =>
  buildPostClassQrItems(props.session, props.viewerLangCodes, props.sourceLangCode),
)

const hints = computed(() =>
  resolvePostClassHints(props.session, props.viewerLangCodes, props.sourceLangCode),
)

const drawerTitle = computed(() => {
  if ((props.session.status || '').toLowerCase() === 'ended') {
    return '课后资料'
  }
  return '下载资料'
})

const drawerHeight = computed(() => {
  if (!enableQrDownload.value) {
    return '36%'
  }
  const n = qrItems.value.length
  if (n <= 1) return '42%'
  if (n === 2) return '55%'
  return '68%'
})
</script>

<style scoped>
.post-class-fab {
  position: fixed;
  right: 16px;
  bottom: 24px;
  z-index: 1000;
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #1677ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.post-class-fab:hover {
  background: #4096ff;
}

.post-class-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
}

.hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 6px;
}

.hint--waiting,
.hint--pending {
  background: #fff7e6;
  color: #ad6800;
}

.hint--partial {
  background: #fff1f0;
  color: #a8071a;
}

.empty {
  color: #8c8c8c;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
}

.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 8px 0 4px;
}

.qr-label {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
}
</style>
